import { Suspense, lazy, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import PreFaceScanFlow from "./components/PreScanFlow";
import ScanInstructions from "../BinahScanFlow/components/ScanInstructions";
import { useTranslation } from "react-i18next";
import FlowHeader from "../../../Common/components/FlowHeader";
import { Stack } from "@mui/material";
import FullscreenLoadingIndicator from "../../../Common/components/FullscreenLoadingIndicator";
import { detect } from "detect-browser";
import UnsupportedDeviceError from "./components/UnsupportedBrowserError";
import GenericError from "./components/GenericError";
import UserTypeSelectionScreen from "./components/UserTypeSelectionScreen";
import { useSearchParams } from "react-router-dom";
import {
  HealthCheckQuestionnaireData,
  submitPublicFaceScanData,
} from "@/services/core-api-adapter";

export enum QuestionType {
  PersonalInformation = "PersonalInformation",
  UserCreated = "UserCreated",
  SexAtBirth = "SexAtBirth",
  Height = "Height",
  Weight = "Weight",
  SmokingStatus = "SmokingStatus",
}

export type QuestionTypeName = QuestionType;

export const CUSTOMER_FLOW: QuestionTypeName[] = [
  QuestionType.PersonalInformation,
  QuestionType.SexAtBirth,
  QuestionType.Height,
  QuestionType.Weight,
  QuestionType.SmokingStatus,
];

export const TEST_USER_FLOW: QuestionTypeName[] = [
  QuestionType.PersonalInformation,
  QuestionType.UserCreated,
  QuestionType.SexAtBirth,
  QuestionType.Height,
  QuestionType.Weight,
  QuestionType.SmokingStatus,
];

export const SKIPPABLE_STEPS: QuestionTypeName[] = [
  QuestionType.SexAtBirth,
  QuestionType.Height,
  QuestionType.Weight,
  QuestionType.SmokingStatus,
];

const ENABLE_CAMERA_SWITCH = detect()?.os === "iOS";

export function getFlowForUserType(
  userType: UserType | null
): QuestionTypeName[] {
  return userType === UserType.TEST_USER ? TEST_USER_FLOW : CUSTOMER_FLOW;
}

const BinahMeasure = lazy(() => import("../BinahScanFlow/components/Measure"));

enum ErrorMap {
  UNSUPPORTED_BROWSER_IOS = "UNSUPPORTED_BROWSER_IOS",
  UNSUPPORTED_BROWSER_ANDROID = "UNSUPPORTED_BROWSER_ANDROID",
}

interface DeviceCompatibilityErrorProps {
  errorType: any;
  onComeBackLater: () => void;
  onTryAgain?: () => void;
}

function DeviceCompatibilityError({
  errorType,
  onComeBackLater,
  onTryAgain,
}: DeviceCompatibilityErrorProps) {
  const navigate = useNavigate();

  switch (errorType) {
    case ErrorMap.UNSUPPORTED_BROWSER_IOS:
      return (
        <UnsupportedDeviceError
          onComeBackLater={onComeBackLater}
          deviceOS="IOS"
        />
      );
    case ErrorMap.UNSUPPORTED_BROWSER_ANDROID:
      return (
        <UnsupportedDeviceError
          onComeBackLater={onComeBackLater}
          deviceOS="ANDROID_OS"
        />
      );
  }

  const onGenericErrorTryAgain = () => {
    if (onTryAgain) {
      return onTryAgain;
    } else {
      return navigate("/home");
    }
  };

  return (
    <GenericError
      onComeBackLater={onComeBackLater}
      onTryAgain={onGenericErrorTryAgain}
    />
  );
}

interface PublicBinahScanFlowProps {
  onCompletedFlow?: () => void;
}

enum BinahScanFlowStates {
  USER_TYPE_SELECTION = "USER_TYPE_SELECTION",
  LOADING_SCAN_PROMPT = "LOADING_SCAN_PROMPT",
  PRE_SCAN_QUESTIONNAIRE = "PRE_SCAN_QUESTIONNAIRE",
  PRE_SCAN_INSTRUCTIONS = "PRE_SCAN_INSTRUCTIONS",
  START_SCAN = "START_SCAN",
}

export enum UserType {
  CUSTOMER = "CUSTOMER",
  TEST_USER = "TEST_USER",
}

export default function PublicBinahScanFlow(_props: PublicBinahScanFlowProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [deviceCompatibilityError, setDeviceCompatibilityError] =
    useState<ErrorMap | null>(null);
  const [questionStepIndex, setQuestionStepIndex] = useState<number>(0);

  const [binahScanFlowState, setBinahScanFlowState] =
    useState<BinahScanFlowStates>(BinahScanFlowStates.LOADING_SCAN_PROMPT);

  const [healthCheckQuestionnaireData, setHealthCheckQuestionnaireData] =
    useState<HealthCheckQuestionnaireData | null>(null);
  const [userType, setUserType] = useState<UserType | null>(null);
  const [preScanSubmitError, setPreScanSubmitError] = useState(false);

  function handleUserTypeSelection(selectedUserType: UserType) {
    setUserType(selectedUserType);
    setBinahScanFlowState(BinahScanFlowStates.PRE_SCAN_QUESTIONNAIRE);
  }

  function onPreScanComplete(subjectDemographic: any) {
    setHealthCheckQuestionnaireData(subjectDemographic);
    setBinahScanFlowState(BinahScanFlowStates.PRE_SCAN_INSTRUCTIONS);
  }

  const skipScanPromptQueryParam = "skipScanPrompt";

  useEffect(() => {
    const device = detect();
    const deviceBrowser = device?.name || "unknown";
    const deviceOsNormalised =
      device?.os?.toUpperCase().split(" ").join("_") || "";

    const isIOSWithNonSafariBrowser =
      deviceOsNormalised === "IOS" && deviceBrowser !== "ios";
    const isAndroidWithNonChromeBrowser =
      deviceOsNormalised === "ANDROID_OS" && deviceBrowser !== "chrome";

    const incompatibleDeviceOsAndBrowser =
      isAndroidWithNonChromeBrowser || isIOSWithNonSafariBrowser;

    if (isIOSWithNonSafariBrowser) {
      setDeviceCompatibilityError(ErrorMap.UNSUPPORTED_BROWSER_IOS);
    }

    if (isAndroidWithNonChromeBrowser) {
      setDeviceCompatibilityError(ErrorMap.UNSUPPORTED_BROWSER_ANDROID);
    }

    if (incompatibleDeviceOsAndBrowser) {
      onDecline();
    }

    if (!incompatibleDeviceOsAndBrowser) {
      if (location.state?.showInstructionsScreen) {
        setBinahScanFlowState(BinahScanFlowStates.PRE_SCAN_INSTRUCTIONS);
      } else if (location.state?.startScanConfirmed) {
        setBinahScanFlowState(BinahScanFlowStates.PRE_SCAN_QUESTIONNAIRE);
      } else if (searchParams.get(skipScanPromptQueryParam)) {
        setBinahScanFlowState(BinahScanFlowStates.PRE_SCAN_QUESTIONNAIRE);
      } else {
        setBinahScanFlowState(BinahScanFlowStates.USER_TYPE_SELECTION);
      }
    }
  }, []);

  function onCompletedScan() {
    setQuestionStepIndex(0);
    setPreScanSubmitError(false);
    setBinahScanFlowState(BinahScanFlowStates.USER_TYPE_SELECTION);
  }

  function onScanRetry() {
    setBinahScanFlowState(BinahScanFlowStates.PRE_SCAN_INSTRUCTIONS);
  }

  function onConfirmScanInstructions() {
    setBinahScanFlowState(BinahScanFlowStates.START_SCAN);
  }

  function onDecline() {
    onCompletedScan();
  }

  function onBackButtonClick() {
    setBinahScanFlowState(BinahScanFlowStates.PRE_SCAN_QUESTIONNAIRE);
  }

  function handleNextStep() {
    const flow = getFlowForUserType(userType);
    if (questionStepIndex < flow.length - 1) {
      setQuestionStepIndex((prev) => prev + 1);
    }
    // On the last step, PreScanFlow calls onComplete (onPreScanComplete) instead of onNextStep
  }

  function handleSkipStep() {
    const flow = getFlowForUserType(userType);
    if (questionStepIndex < flow.length - 1) {
      setQuestionStepIndex((prev) => prev + 1);
    }
  }

  function handlePreviousStep() {
    if (questionStepIndex > 0) {
      setQuestionStepIndex((prev) => prev - 1);
    } else {
      setBinahScanFlowState(BinahScanFlowStates.USER_TYPE_SELECTION);
    }
  }

  if (deviceCompatibilityError) {
    return (
      <DeviceCompatibilityError
        onComeBackLater={onDecline}
        errorType={deviceCompatibilityError}
      />
    );
  }

  return (
    <>
      <Stack sx={{ height: "100%", bgcolor: "background.subtle" }}>
        {binahScanFlowState === BinahScanFlowStates.USER_TYPE_SELECTION && (
          <UserTypeSelectionScreen
            onCustomerClick={() => handleUserTypeSelection(UserType.CUSTOMER)}
            onTestUserClick={() => handleUserTypeSelection(UserType.TEST_USER)}
          />
        )}
        {binahScanFlowState === BinahScanFlowStates.LOADING_SCAN_PROMPT && (
          <FullscreenLoadingIndicator mainIconType="face" />
        )}
        {binahScanFlowState !== BinahScanFlowStates.START_SCAN &&
          binahScanFlowState !== BinahScanFlowStates.PRE_SCAN_INSTRUCTIONS &&
          binahScanFlowState !== BinahScanFlowStates.USER_TYPE_SELECTION &&
          !preScanSubmitError && (
            <FlowHeader
              title={t("MemberRequiredActionsFlow.BINAH_SCAN.title")}
              value={questionStepIndex + 1}
              max={getFlowForUserType(userType).length}
              isBackButtonVisible={true}
              onBackButtonClick={handlePreviousStep}
              inheritBackground={true}
            />
          )}

        {binahScanFlowState === BinahScanFlowStates.PRE_SCAN_QUESTIONNAIRE && (
          <PreFaceScanFlow
            stepIndex={questionStepIndex}
            onNextStep={handleNextStep}
            onSkipStep={handleSkipStep}
            onComplete={onPreScanComplete}
            userType={userType}
            onComeBackLater={onDecline}
            onSubmitErrorChange={setPreScanSubmitError}
          />
        )}

        {binahScanFlowState === BinahScanFlowStates.PRE_SCAN_INSTRUCTIONS && (
          <ScanInstructions
            onConfirm={onConfirmScanInstructions}
            onComeBackLater={onDecline}
            onBackButtonClick={onBackButtonClick}
          />
        )}

        {binahScanFlowState === BinahScanFlowStates.START_SCAN && (
          <Suspense
            fallback={<FullscreenLoadingIndicator mainIconType="face" />}
          >
            <BinahMeasure
              onCompletedScan={onCompletedScan}
              onScanRetry={onScanRetry}
              healthCheckQuestionnaireData={healthCheckQuestionnaireData}
              onComeBackLaterButtonClick={onDecline}
              onPublicFlowSubmitData={(data) => {
                submitPublicFaceScanData(data).catch((err) => {
                  console.error("Failed to submit public face scan data", err);
                });
              }}
              onPublicFlowStopMeasuring={onCompletedScan}
              showPublicFlowResult
              enableCameraSwitch={ENABLE_CAMERA_SWITCH}
            />
          </Suspense>
        )}
      </Stack>
    </>
  );
}
